import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getPriceTypeLabel, priceTypeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateToolMutation, useUpdateToolMutation } from '../../../../hooks/mutations/useToolMutation';
import { useToolsTypesQuery } from '../../../../hooks/queries/useToolTypesQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { Tool } from '../../../../services/apiSLTP/materials/tools';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomDateInput from '../../../ui/CustomDateInput';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    tool: Tool | null;
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateToolModal: React.FC<ModalProps> = ({ tool, showEditModal, setShowEditModal, isEditMode = true, refetch }) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();

    const [designation, setDesignation] = useState(tool?.designation || '');
    const [codeProC, setCodeProC] = useState(tool?.codeProC || '');
    const [isActive, setIsActive] = useState(tool ? tool?.crudStatus === crudStatusEnum.OK : true);
    const [selectedToolType, setSelectedToolType] = useState({ label: '', value: '' });
    const [price, setPrice] = useState(typeof tool?.price === 'number' ? tool?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        tool?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeLabel(tool?.priceType))),
                  value: (tool?.priceType).toString(),
              }
            : null
    );
    const [model, setModel] = useState(tool?.model || '');
    const [baseName, setBaseName] = useState(tool?.baseName || '');
    const [startDate, setStartDate] = useState<Date | null>(tool?.startDate ? new Date(tool.startDate) : null);
    const [endDate, setEndDate] = useState<Date | null>(tool?.endDate ? new Date(tool.endDate) : null);

    const { data: dataToolTypes, isLoading: isToolTypesLoading } = useToolsTypesQuery({});

    useEffect(() => {
        const toolType = dataToolTypes?.find((type) => type._id === tool?.toolTypeId?._id);
        if (toolType) setSelectedToolType({ label: toolType.designation, value: toolType._id });
    }, [dataToolTypes]);

    const { mutate: mutateTool } = useUpdateToolMutation({ onSuccess: refetch });
    const { mutate: addTool } = useCreateToolMutation({ onSuccess: refetch });

    const handleSubmit = () => {
        const toolData = {
            designation,
            codeProC,
            crudStatus: isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE,
            toolTypeId: selectedToolType.value,
            priceType: priceType ? parseInt(priceType.value) : null,
            price: parseFloat(price),
            model,
            baseName,
            startDate: startDate ? startDate.toISOString() : null,
            endDate: endDate ? endDate.toISOString() : null,
        };

        if (isEditMode) {
            mutateTool({ toolId: tool?._id || '', data: toolData });
        } else {
            addTool(toolData);
        }
    };

    const handleActiveChange = (checked: boolean) => setIsActive(checked);

    const isFormValid = () => {
        return designation && codeProC && selectedToolType.value && baseName;
    };

    return (
        <ModalBody
            title={t('tool')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            disableSubmit={!isFormValid()}
            handleClose={() => setShowEditModal(false)}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={setDesignation} required />
            <CustomTextInput value={codeProC} label={t('codeProC')} onChange={setCodeProC} required />
            <ToggleLine checked={isActive} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput value={baseName} onChange={setBaseName} label={t('baseName')} required />
            <DropDown
                items={dataToolTypes?.map((type) => ({ label: type.designation, value: type._id })) || []}
                selectedItem={selectedToolType}
                handleChange={(e, v) => setSelectedToolType(v || { label: '', value: '' })}
                inputLabel={t('type')}
                required
                loading={isToolTypesLoading}
            />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(priceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    setPriceType(value);
                }}
                inputLabel={t('pricing')}
                disableClearable={false}
            />
            <CustomDateInput
                label={t('startDate')}
                onChange={(value) => (value ? setStartDate(new Date(value?.toISOString())) : null)}
                defaultDate={startDate}
            />
            <CustomDateInput
                label={t('endDate')}
                onChange={(value) => (value ? setEndDate(new Date(value?.toISOString())) : null)}
                defaultDate={endDate}
            />
            <CustomTextInput value={model} onChange={setModel} label={t('model')} />
        </ModalBody>
    );
};

export default UpdateToolModal;
